import React, { useState, useEffect } from 'react';
import { Modal, Input, Button, Form, Row, Col, Typography, Alert } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Tooltip, Label } from 'components';
import {
  customRound,
  formatCurrency,
  parseCurrency,
} from '../../utils/helpers';
import '../../styles/components/_businessSizeCalc.scss';

const { Text } = Typography;

const BusinessSizeCalculator = ({
  visible,
  onClose,
  onSave,
  initialValues = {
    annualTurnover: '',
    annualBalance: '',
    numberOfEmployees: '',
  },
}) => {
  const [form] = Form.useForm();
  const [result, setResult] = useState(null);
  const [calculationDone, setCalculationDone] = useState(false);

  // reset form when modal opens
  useEffect(() => {
    if (visible) {
      form.resetFields();
      setResult(null);
      setCalculationDone(false);
    }
  }, [visible, form]);

  // simplified currency formatter - only formats when user leaves the field
  const handleCurrencyBlur = (e, fieldName) => {
    const value = e.target.value;
    if (!value || value === '') return;

    const formattedValue = formatCurrency(value);
    if (formattedValue !== value) {
      form.setFieldsValue({ [fieldName]: formattedValue });
    }
  };

  const handleCalculate = () => {
    form.validateFields().then(values => {
      const { annualTurnover, annualBalance, numberOfEmployees } = values;

      // Convert to numbers
      const turnover = parseCurrency(annualTurnover);
      const balance = parseCurrency(annualBalance);
      const employees = parseInt(numberOfEmployees);

      // logic
      let businessSize;

      if (employees < 10 && (turnover <= 2000000 || balance <= 2000000)) {
        businessSize = 'Πολύ Μικρή';
      } else if (
        employees < 50 &&
        (turnover <= 10000000 || balance <= 10000000)
      ) {
        businessSize = 'Μικρή';
      } else if (
        employees < 250 &&
        (turnover <= 50000000 || balance <= 43000000)
      ) {
        businessSize = 'Μεσαία';
      } else {
        businessSize = 'Μεγάλη';
      }

      setResult(businessSize);
      setCalculationDone(true);
    });
  };

  const handleSave = () => {
    if (result) {
      onSave(result);
      handleClose();
    }
  };

  const handleClose = () => {
    form.resetFields();
    setResult(null);
    setCalculationDone(false);
    onClose();
  };

  const handleRecalculate = () => {
    // Reset form to initial empty values
    form.resetFields();
    form.setFieldsValue({
      annualTurnover: '',
      annualBalance: '',
      numberOfEmployees: '',
    });
    setCalculationDone(false);
    setResult(null);
  };

  return (
    <Modal
      title={
        <div className="business-size-calculator-title">
          Βρες το μέγεθος της επιχείρησης
          <Tooltip
            title="Υπολογισμός μεγέθους επιχείρησης βάσει αριθμού εργαζομένων και οικονομικών στοιχείων"
            placement="right"
          >
            <InfoCircleOutlined />
          </Tooltip>
        </div>
      }
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={600}
      bodyStyle={{ padding: '24px' }}
      className="business-size-calculator-modal"
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={initialValues}
        className="business-size-calculator"
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="annualTurnover"
              label={<Label label="Ετήσιος Κύκλος Εργασιών" asterisk={true} />}
              rules={[
                { required: true, message: 'Το πεδίο είναι υποχρεωτικό' },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();

                    // Remove only the € symbol, keep decimal part
                    const cleanValue = value.replace(/€/g, '');

                    // Allow both dots and commas in the validation regex
                    if (!/^[0-9.,]+$/.test(cleanValue)) {
                      return Promise.reject(
                        'Παρακαλώ εισάγετε έναν έγκυρο αριθμό'
                      );
                    }

                    const parsedValue = parseCurrency(value);
                    if (isNaN(parsedValue) || parsedValue < 0) {
                      return Promise.reject(
                        'Παρακαλώ εισάγετε έναν έγκυρο θετικό αριθμό'
                      );
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                placeholder="0,00€"
                onBlur={e => handleCurrencyBlur(e, 'annualTurnover')}
                className="business-size-calculator-input"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="annualBalance"
              label={<Label label="Ετήσιος Ισολογισμός" asterisk={true} />}
              rules={[
                { required: true, message: 'Το πεδίο είναι υποχρεωτικό' },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();

                    // Remove only the € symbol, keep decimal part
                    const cleanValue = value.replace(/€/g, '');

                    // Allow both dots and commas in the validation regex
                    if (!/^[0-9.,]+$/.test(cleanValue)) {
                      return Promise.reject(
                        'Παρακαλώ εισάγετε έναν έγκυρο αριθμό'
                      );
                    }

                    const parsedValue = parseCurrency(value);
                    if (isNaN(parsedValue) || parsedValue < 0) {
                      return Promise.reject(
                        'Παρακαλώ εισάγετε έναν έγκυρο θετικό αριθμό'
                      );
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                placeholder="0,00€"
                onBlur={e => handleCurrencyBlur(e, 'annualBalance')}
                className="business-size-calculator-input"
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Form.Item
              name="numberOfEmployees"
              label={<Label label="Αριθμός Υπαλλήλων" asterisk={true} />}
              rules={[
                { required: true, message: 'Το πεδίο είναι υποχρεωτικό' },
                {
                  pattern: /^[0-9]+$/,
                  message: 'Παρακαλώ εισάγετε έναν έγκυρο αριθμό',
                },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    const num = parseInt(value);
                    if (isNaN(num) || num < 0) {
                      return Promise.reject(
                        'Ο αριθμός δεν μπορεί να είναι αρνητικός'
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                placeholder="Εισάγετε Αριθμό Υπαλλήλων"
                className="business-size-calculator-input"
              />
            </Form.Item>
          </Col>
        </Row>

        {calculationDone && (
          <div className="business-size-calculator-result">
            <div className="business-size-calculator-result-header">
              Αποτέλεσμα
            </div>
            <div className="business-size-calculator-result-content">
              <Text>
                Σύμφωνα με τα παραπάνω στοιχεία η επιχείρησή σας αποτελεί{' '}
                <Text strong>{result} Επιχείρηση</Text>.
              </Text>
            </div>
          </div>
        )}

        {/* Custom footer */}
        <div className="business-size-calculator-footer">
          {calculationDone ? (
            <>
              <Button
                id="calc-button"
                onClick={handleRecalculate}
                className="business-size-calculator-recalculate-button"
              >
                Καθαρισμός
              </Button>
              <Button
                id="save-button-bus"
                type="primary"
                onClick={handleSave}
                className="business-size-calculator-save-button"
              >
                Καταχώρηση
              </Button>
            </>
          ) : (
            <>
              <Button
                id="calc-button"
                type="primary"
                onClick={handleCalculate}
                className="business-size-calculator-calculate-button"
              >
                Υπολογισμός
              </Button>
              <Button
                id="save-button-bus"
                type="primary"
                disabled={true}
                className="business-size-calculator-disabled-save-button"
              >
                Καταχώρηση
              </Button>
            </>
          )}
        </div>
      </Form>
    </Modal>
  );
};

export default BusinessSizeCalculator;
